import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { analyzeSign, type SignedInterval, type Sign } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { FUNCTION_AREA_DEFAULT_PARAMS, type FunctionAreaParams } from './function-area.model';

/**
 * Creates area visualization between a function and the x-axis.
 *
 * Features:
 * - Shaded area between the function curve and x-axis
 * - Vertical boundary lines at xStart and xEnd (dashed, indigo by default)
 * - Optional signed areas: positive regions (green) and negative regions (red)
 * - Handles reversed bounds (xStart > xEnd) by swapping and inverting sign colors
 *
 * Uses JSXGraph's integral element for the area visualization.
 * For signed areas, uses analyzeSign from analytics to find zero crossings
 * and creates separate integral elements for positive and negative regions.
 */
export class FunctionAreaCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'functionArea';

  /** Maximum number of signed intervals to display */
  private readonly maxIntervals = 20;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, featureTheme } = context;
    const params = def.params as FunctionAreaParams | undefined;

    if (!params?.of) {
      throw new Error('FunctionArea feature requires params.of to specify the parent function');
    }
    if (params.xStart === undefined || params.xEnd === undefined) {
      throw new Error('FunctionArea feature requires params.xStart and params.xEnd');
    }

    const parentFn = this.getEvaluableParent(params.of, context);
    const objects: GeometryElement[] = [];

    // Mutable state for reactive params
    const state = {
      xStart: params.xStart,
      xEnd: params.xEnd,
    };

    // Helper to get ordered bounds and check if reversed
    const getBounds = () => {
      const reversed = state.xStart > state.xEnd;
      const xMin = reversed ? state.xEnd : state.xStart;
      const xMax = reversed ? state.xStart : state.xEnd;
      return { xMin, xMax, reversed };
    };

    // Helper to get effective sign (inverted if bounds are reversed)
    const getEffectiveSign = (sign: Sign, reversed: boolean): Sign => {
      if (!reversed) {
        return sign;
      }
      // Invert sign when bounds are reversed
      if (sign === 'positive') {
        return 'negative';
      }
      if (sign === 'negative') {
        return 'positive';
      }
      return sign;
    };

    const signed = params.signed ?? FUNCTION_AREA_DEFAULT_PARAMS.signed;

    // Resolve styles
    const polygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, []);
    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display, []);
    const pointStyle = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Resolve signed area styles
    const positivePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['positive']);
    const negativePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['negative']);

    // Create the function curve for JSXGraph integral element
    const fnCurve = board.create('functiongraph', [parentFn.evaluate], { visible: false }) as GeometryElement;
    objects.push(fnCurve);

    if (signed) {
      // For signed areas, create separate integrals for positive and negative regions
      // Cache intervals for performance
      let cachedIntervals: SignedInterval[] = [];
      let cacheKey = '';
      let cachedReversed = false;

      const getIntervals = (): { intervals: SignedInterval[]; reversed: boolean } => {
        const { xMin, xMax, reversed } = getBounds();
        const fnParams =
          'getParams' in parentFn ? (parentFn as { getParams: () => Record<string, number> }).getParams() : {};
        const paramsKey = Object.values(fnParams)
          .map((v) => v.toFixed(4))
          .join(',');
        const newKey = `${xMin.toFixed(2)},${xMax.toFixed(2)}|${paramsKey}`;

        if (newKey !== cacheKey) {
          cacheKey = newKey;
          cachedReversed = reversed;
          cachedIntervals = analyzeSign(parentFn.evaluate, xMin, xMax, {
            maxResults: this.maxIntervals,
          });
        }
        return { intervals: cachedIntervals, reversed: cachedReversed };
      };

      const getInterval = (index: number): { interval: SignedInterval | null; reversed: boolean } => {
        const { intervals, reversed } = getIntervals();
        return {
          interval: index < intervals.length ? intervals[index] : null,
          reversed,
        };
      };

      // Create placeholder integrals for each potential interval
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        const integral = board.create(
          'integral',
          [[() => getInterval(index).interval?.start ?? NaN, () => getInterval(index).interval?.end ?? NaN], fnCurve],
          {
            fillColor: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 'transparent';
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillColor ? context.themeService.getColor(style.fillColor) : 'transparent';
            },
            fillOpacity: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 0;
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillOpacity ?? 0.3;
            },
            visible: () => {
              const { interval } = getInterval(index);
              return visible && interval !== null;
            },
            withLabel: false,
            curveLeft: { visible: false },
            curveRight: { visible: false },
            baseLeft: { visible: false },
            baseRight: { visible: false },
            label: { visible: false },
          },
        ) as GeometryElement;

        objects.push(integral);
      }
    } else {
      // Single unsigned area - use reactive functions for bounds
      const fillColor = context.themeService.getColor(polygonStyle.fillColor ?? 'indigo');

      const integral = board.create('integral', [[() => getBounds().xMin, () => getBounds().xMax], fnCurve], {
        fillColor,
        fillOpacity: polygonStyle.fillOpacity ?? 0.3,
        visible,
        withLabel: false,
        curveLeft: { visible: false },
        curveRight: { visible: false },
        baseLeft: { visible: false },
        baseRight: { visible: false },
        label: { visible: false },
      }) as GeometryElement;

      objects.push(integral);
    }

    // Create boundary lines at xStart and xEnd
    const strokeColor = context.themeService.getColor(lineStyle.color ?? 'indigo');
    const pointColor = context.themeService.getColor(pointStyle.color ?? 'indigo');
    const dash = lineStyle.dash ?? 3;
    const strokeWidth = lineStyle.strokeWidth ?? 2;
    const pointVisible = pointStyle.visible ?? false;

    // Left boundary line (xStart) - use reactive functions
    const leftLineStart = board.create('point', [() => state.xStart, 0], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(leftLineStart);

    const leftLineEnd = board.create('point', [() => state.xStart, () => parentFn.evaluate(state.xStart)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(leftLineEnd);

    const leftLine = board.create('line', [leftLineStart, leftLineEnd], {
      straightFirst: false,
      straightLast: false,
      strokeColor,
      strokeWidth,
      dash,
      visible,
    }) as GeometryElement;
    objects.push(leftLine);

    // Right boundary line (xEnd) - use reactive functions
    const rightLineStart = board.create('point', [() => state.xEnd, 0], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(rightLineStart);

    const rightLineEnd = board.create('point', [() => state.xEnd, () => parentFn.evaluate(state.xEnd)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(rightLineEnd);

    const rightLine = board.create('line', [rightLineStart, rightLineEnd], {
      straightFirst: false,
      straightLast: false,
      strokeColor,
      strokeWidth,
      dash,
      visible,
    }) as GeometryElement;
    objects.push(rightLine);

    return {
      id,
      type: 'functionArea',
      objects,
      getParams: () => ({ xStart: state.xStart, xEnd: state.xEnd }),
      setParams: (newParams) => {
        if (newParams['xStart'] !== undefined) {
          state.xStart = newParams['xStart'];
        }
        if (newParams['xEnd'] !== undefined) {
          state.xEnd = newParams['xEnd'];
        }
        board.update();
      },
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
